import { hydrateRoot } from 'react-dom/client';

import { createEntryPoint } from '@xing-com/crate-runtime';
import type { ErrorReporter } from '@xing-com/crate-xinglet';
import type {
  BrowserRuntime,
  GlobalScope,
  Runtime,
} from '@xing-com/crate-xinglet/internal';

declare const globalThis: GlobalScope & {
  crateRuntime: Runtime;
  document: typeof document;
};

export async function render(
  runtime: BrowserRuntime,
  {
    reportError,
    startXinglets = [],
  }: {
    reportError: ErrorReporter;
    startXinglets: Array<string>;
  }
): Promise<void> {
  const { enableMocks = false, entryPoint, manifestId } = runtime.config;

  globalThis.crateRuntime = runtime;

  if (enableMocks) {
    // eslint-disable-next-line node/no-unsupported-features/es-syntax
    const { setupMocking } = await import('./mocks');
    await setupMocking(runtime.config.mockSessionId);
  }

  // [SSR] if we have startXinglets to load, we do trigger this
  // this is required, since react needs to have all code available to
  // hydrate the server DOM
  await Promise.all(
    [entryPoint, ...startXinglets].map(async (name) => {
      await runtime.loadXinglet(name);
    })
  );

  const mountpoint = globalThis.document.querySelector('#app[data-mountpoint]');
  if (!mountpoint) {
    throw new Error('No mountpoint found');
  }

  hydrateRoot(mountpoint, createEntryPoint(runtime, entryPoint), {
    onRecoverableError(error, errorInfo) {
      if (!(error instanceof Error)) {
        reportError(String(error));

        return;
      }

      if (error.message.startsWith(knownSuspenseError)) {
        return;
      }

      reportError(error, errorInfo);
    },
  });

  mountpoint.setAttribute('data-mounted', '');

  if (LOCAL_DEVELOPMENT || manifestId === 'dev') {
    const { startDevMode } = await import('./dev-mode');

    startDevMode(runtime);
  }
}

const knownSuspenseError = 'The server did not finish this Suspense boundary';
